import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  fetchGalleryCloudinaryImages,
  fetchGalleryCloudinaryFolders,
  CloudinaryImage,
  CloudinaryFolder
} from '../../utils/CloudinaryAPI';
import './CloudinaryImageBrowser.css';
import BrandMarkSpinner from '../Loading/BrandMarkSpinner';

interface CloudinaryImageBrowserProps {
  onImagesSelected: (images: CloudinaryImage[]) => void;
  onClose: () => void;
  existingImages?: string[];
  maxSelection?: number;
  insideModal?: boolean;
  onContinue?: () => void;
  showContinueButton?: boolean;
}

const CloudinaryImageBrowser: React.FC<CloudinaryImageBrowserProps> = ({
  onImagesSelected,
  onClose,
  existingImages = [],
  maxSelection = 20,
  insideModal = false,
  onContinue,
  showContinueButton = false
}) => {
  const [cloudinaryImages, setCloudinaryImages] = useState<CloudinaryImage[]>([]);
  const [cloudinaryFolders, setCloudinaryFolders] = useState<CloudinaryFolder[]>([]);
  const [selectedImages, setSelectedImages] = useState<CloudinaryImage[]>([]);
  const [loadingFolders, setLoadingFolders] = useState(true);
  const [loadingImages, setLoadingImages] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showOnlyUnused, setShowOnlyUnused] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<string>('');

  const loadCloudinaryFolders = React.useCallback(async () => {
    try {
      setLoadingFolders(true);
      const folders = await fetchGalleryCloudinaryFolders();
      const candidateFolders = folders.filter((folder) => folder.imageCount > 0);

      // Validate each folder has at least one currently retrievable asset.
      // This prevents stale/deleted folders from lingering in the dropdown.
      const validatedFolders = await Promise.all(
        candidateFolders.map(async (folder) => {
          try {
            const oneImage = await fetchGalleryCloudinaryImages(folder.path, 1);
            return oneImage.length > 0 ? folder : null;
          } catch (_) {
            return null;
          }
        })
      );

      const activeFolders = validatedFolders.filter((folder): folder is CloudinaryFolder => Boolean(folder));
      setCloudinaryFolders(activeFolders);
      setSelectedFolder((prev) => {
        if (!prev) return prev;
        const stillExists = activeFolders.some((folder) => folder.path === prev);
        if (!stillExists) {
          setCloudinaryImages([]);
          setSelectedImages([]);
          return '';
        }
        return prev;
      });
      setLoadingFolders(false);
    } catch (error) {
      console.error('Error loading Cloudinary folders:', error);
      setCloudinaryFolders([]);
      setSelectedFolder('');
      setCloudinaryImages([]);
      setSelectedImages([]);
      setLoadingFolders(false);
    }
  }, []);

  useEffect(() => {
    loadCloudinaryFolders();
  }, [loadCloudinaryFolders]);

  const loadImagesForFolder = React.useCallback(async (folderPath: string) => {
    try {
      if (!folderPath) {
        setCloudinaryImages([]);
        return;
      }

      setLoadingImages(true);
      const images = await fetchGalleryCloudinaryImages(folderPath);
      setCloudinaryImages(images);
      setLoadingImages(false);
    } catch (error) {
      console.error('Error loading Cloudinary images:', error);
      setCloudinaryImages([]);
      setLoadingImages(false);
    }
  }, []);

  // Filter images based on search, folder, and usage status
  const allFilteredImages = cloudinaryImages.filter(img => {
    const matchesSearch = !searchQuery || 
      (img.filename && img.filename.toLowerCase().includes(searchQuery.toLowerCase())) ||
      img.publicId.toLowerCase().includes(searchQuery.toLowerCase());
    
    const isUsed = existingImages.includes(img.publicId);
    const matchesUsage = !showOnlyUnused || !isUsed;
    
    return matchesSearch && matchesUsage;
  });

  const filteredImages = allFilteredImages;
  const allFilteredSelected = filteredImages.length > 0 && filteredImages.every((img) =>
    selectedImages.some((selected) => selected.publicId === img.publicId)
  );
  const selectableFilteredImages = filteredImages.slice(0, Math.max(maxSelection, 0));
  const canSelectAll = selectableFilteredImages.length > 0;

  const toggleImageSelection = (image: CloudinaryImage) => {
    setSelectedImages(prev => {
      const isSelected = prev.some(img => img.publicId === image.publicId);
      if (isSelected) {
        return prev.filter(img => img.publicId !== image.publicId);
      } else if (prev.length < maxSelection) {
        return [...prev, image];
      }
      return prev;
    });
  };

  const handleAddImages = () => {
    onImagesSelected(selectedImages);
    onClose();
  };

  const handleRefreshImages = () => {
    loadCloudinaryFolders();
    if (selectedFolder) {
      loadImagesForFolder(selectedFolder);
    }
  };

  const handleSelectAll = () => {
    if (!canSelectAll) return;
    setSelectedImages(selectableFilteredImages);
  };

  const handleDeselectAll = () => {
    setSelectedImages([]);
  };

  const mount = (node: React.ReactElement) => (
    insideModal || typeof document === 'undefined' ? node : createPortal(node, document.body)
  );

  if (loadingFolders) {
    return mount(
      <div className={`cloudinary-browser-overlay ${insideModal ? 'inside-modal' : ''}`} onClick={onClose}>
        <div className="cloudinary-browser-modal" onClick={(e) => e.stopPropagation()}>
          <div className="loading-container">
            <BrandMarkSpinner size="medium" label="Loading Cloudinary folders" caption />
          </div>
        </div>
      </div>
    );
  }

  return mount(
    <div className={`cloudinary-browser-overlay ${insideModal ? 'inside-modal' : ''}`} onClick={onClose}>
      <div className="cloudinary-browser-modal" onClick={(e) => e.stopPropagation()}>
        
        {!insideModal && (
          <div className="cloudinary-browser-header">
            <h3>Select Images from Cloudinary</h3>
            <button className="close-browser-btn" onClick={onClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}

        <div className="browser-controls">
          <div className="search-container">
            <input
              type="text"
              placeholder="Search images or folders..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            <i className="fas fa-search search-icon"></i>
          </div>

          <div className="button-group">
            <button 
              className="refresh-btn"
              onClick={handleRefreshImages}
              title="Refresh images from Cloudinary"
            >
              <i className="fas fa-sync-alt"></i>
              Refresh
            </button>
            <button
              className="bulk-select-btn"
              onClick={handleSelectAll}
              disabled={!selectedFolder || loadingImages || !canSelectAll || allFilteredSelected}
              title={maxSelection > 0 ? `Select up to ${maxSelection} images from current results` : 'Select all images in current results'}
            >
              <i className="fas fa-check-double"></i>
              Select All
            </button>
            <button
              className="bulk-deselect-btn"
              onClick={handleDeselectAll}
              disabled={selectedImages.length === 0}
              title="Clear all selected images"
            >
              <i className="fas fa-times-circle"></i>
              Deselect All
            </button>
          </div>

          <div className="filter-container">
            <select
              value={selectedFolder}
              onChange={(e) => {
                const folderPath = e.target.value;
                setSelectedFolder(folderPath);
                setSelectedImages([]);
                loadImagesForFolder(folderPath);
              }}
              className="folder-select"
            >
              <option value="">Select a gallery folder...</option>
              {cloudinaryFolders.map(folder => (
                <option key={folder.path} value={folder.path}>
                  {folder.path} ({folder.imageCount})
                </option>
              ))}
            </select>
          </div>

          <div className="usage-filter">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={showOnlyUnused}
                onChange={(e) => setShowOnlyUnused(e.target.checked)}
              />
              <span className="checkmark"></span>
              Show only unused images
            </label>
          </div>

          {selectedImages.length > 0 && (
            <div className="selection-info">
              <span className="selected-count">{selectedImages.length} selected</span>
              {maxSelection && (
                <span className="max-selection">/ {maxSelection} max</span>
              )}
            </div>
          )}
        </div>

        {!selectedFolder && (
          <div className="loading-container">
            <p>Select a folder to load images from the current website path.</p>
            <small>Folder list comes from metadata only and does not download original images.</small>
          </div>
        )}

        {selectedFolder && loadingImages && (
          <div className="loading-container">
            <BrandMarkSpinner size="medium" label="Loading images from selected folder" caption />
          </div>
        )}

        {selectedFolder && !loadingImages && (
          <div className="cloudinary-images-grid">

          {filteredImages.map((image, index) => {
            const isSelected = selectedImages.some(img => img.publicId === image.publicId);
            const isUsed = existingImages.includes(image.publicId);
            
            return (
              <div
                key={`${image.publicId}-${index}`}
                className={`cloudinary-image-item ${isSelected ? 'selected' : ''} ${isUsed ? 'used' : ''}`}
                onClick={() => toggleImageSelection(image)}
              >
                <div className="image-thumbnail">
                  <img
                    src={(image.secure_url || image.url || '').replace('/upload/', '/upload/w_200,h_200,c_fill,f_auto,q_auto/')}
                    alt={image.alt || image.filename || 'Cloudinary Image'}
                    loading="lazy"
                    onError={() => {
                      // If Cloudinary asset was deleted, remove it from current results.
                      setCloudinaryImages((prev) => prev.filter((img) => img.publicId !== image.publicId));
                      setSelectedImages((prev) => prev.filter((img) => img.publicId !== image.publicId));
                    }}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block'
                    }}
                  />
                  {isSelected && (
                    <div className="selection-indicator">
                      <i className="fas fa-check"></i>
                    </div>
                  )}
                  {isUsed && (
                    <div className="used-indicator">
                      <i className="fas fa-check-circle"></i>
                      <span>In Use</span>
                    </div>
                  )}
                </div>
                
                <div className="image-info">
                  <div className="image-filename" title={image.filename || image.alt || 'Image'}>
                    {(image.filename || image.alt || 'Image').length > 18 
                      ? `${(image.filename || image.alt || 'Image').substring(0, 18)}...` 
                      : (image.filename || image.alt || 'Image')}
                  </div>
                  <div className="image-folder" title={image.folder || 'Root folder'}>
                    <i className="fas fa-folder"></i>
                    {image.folder === 'root' ? 'Root' : image.folder || 'Root'}
                  </div>
                  <div className="image-details">
                    <div className="image-dimensions">
                      <i className="fas fa-expand-arrows-alt"></i>
                      {image.width || 0} × {image.height || 0}
                    </div>
                    <div className="image-format">
                      <i className="fas fa-file-image"></i>
                      {image.secure_url?.split('.').pop()?.toUpperCase() || 'JPG'}
                    </div>
                    <div className="image-size">
                      <i className="fas fa-weight-hanging"></i>
                      {image.bytes ? `${Math.round(image.bytes / 1024)}KB` : 'Unknown'}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          </div>
        )}

        {!insideModal && (
          <div className="browser-actions">
            <button 
              className="cancel-btn" 
              onClick={onClose}
            >
              Cancel
            </button>
            <button 
              className="add-images-btn" 
              onClick={handleAddImages}
              disabled={selectedImages.length === 0}
            >
              Add {selectedImages.length} Image{selectedImages.length !== 1 ? 's' : ''}
            </button>
          </div>
        )}

        {insideModal && (
          <div className="browser-actions">
            <button 
              className="cancel-btn" 
              onClick={onClose}
            >
              Cancel
            </button>
            <button 
              className="add-images-btn" 
              onClick={handleAddImages}
              disabled={selectedImages.length === 0}
            >
              Add {selectedImages.length} Image{selectedImages.length !== 1 ? 's' : ''}
            </button>
          </div>
        )}

        {insideModal && showContinueButton && (
          <button 
            className="floating-continue-button"
            onClick={handleAddImages}
          >
            <i className="fas fa-arrow-right"></i>
            Continue ({selectedImages.length} selected)
          </button>
        )}
      </div>
    </div>
  );
};

export default CloudinaryImageBrowser;
